import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { getState, type UsStateCode } from "@/lib/states";

/**
 * apps/web :: components/dashboard :: StateSwitcher
 *
 * Horizontal tab strip of the user's `targetStates`. Purely presentational:
 * the parent owns the active-state value and listens to `onSelect`.
 *
 * This is NOT the full <StatePicker> — it's an intentionally narrower UI:
 *   - One tab per state the user picked during onboarding (so they aren't
 *     confronted with 5 cards when they only care about 2).
 *   - Active state surfaced via `aria-selected` + visual highlight.
 *   - Keyboard support per the WAI-ARIA tabs pattern: ArrowLeft/Right move
 *     focus between tabs; Enter/Space activates. Tab/Shift-Tab leaves the
 *     tablist entirely.
 *   - Only the active tab is in the natural tab order; non-active tabs are
 *     `tabIndex=-1` (the arrow keys bring focus to them on demand). This is
 *     the standard ARIA tablist pattern — see the onboarding `<StatePicker>`
 *     for the same convention applied to cards.
 *
 * State-name localisation matches `<StatePicker>`: we check
 * `state.<code-lowercase>.name` first and fall through to the catalog's
 * English name.
 */
export interface StateSwitcherProps {
  targetStates: readonly UsStateCode[];
  activeState: UsStateCode;
  onSelect: (next: UsStateCode) => void;
}

export default function StateSwitcher({
  targetStates,
  activeState,
  onSelect,
}: StateSwitcherProps) {
  const { t } = useTranslation();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function focusAt(index: number) {
    const safe = ((index % targetStates.length) + targetStates.length) %
      targetStates.length;
    const code = targetStates[safe];
    if (!code) return;
    tabRefs.current[code]?.focus();
  }

  function handleKey(
    ev: KeyboardEvent<HTMLButtonElement>,
    index: number,
    code: UsStateCode,
  ) {
    switch (ev.key) {
      case "ArrowRight":
        ev.preventDefault();
        focusAt(index + 1);
        return;
      case "ArrowLeft":
        ev.preventDefault();
        focusAt(index - 1);
        return;
      case "Home":
        ev.preventDefault();
        focusAt(0);
        return;
      case "End":
        ev.preventDefault();
        focusAt(targetStates.length - 1);
        return;
      case "Enter":
      case " ":
        ev.preventDefault();
        if (code !== activeState) onSelect(code);
        return;
      default:
        return;
    }
  }

  return (
    <div
      role="tablist"
      aria-label={t("dashboard.stateSwitcher.ariaLabel")}
      data-testid="state-switcher"
      className="flex flex-wrap gap-xs"
    >
      {targetStates.map((code, index) => {
        const state = getState(code);
        const label = t(`state.${code.toLowerCase()}.name`, {
          defaultValue: state?.name ?? code,
        });
        const selected = code === activeState;
        const className = [
          "rounded-md border px-md py-sm text-sm font-semibold transition-colors",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
          selected
            ? "border-brand-primary bg-brand-primary text-white"
            : "border-brand-primary/20 bg-white text-brand-primary hover:bg-brand-primary/5",
        ].join(" ");
        return (
          <button
            key={code}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls="dashboard-rule-diff-panel"
            data-testid={`state-switcher-tab-${code}`}
            data-state-code={code}
            tabIndex={selected ? 0 : -1}
            ref={(el) => {
              tabRefs.current[code] = el;
            }}
            onClick={() => {
              if (code !== activeState) onSelect(code);
            }}
            onKeyDown={(ev) => handleKey(ev, index, code)}
            className={className}
          >
            <span className="mr-xs inline-flex h-5 min-w-[1.75rem] items-center justify-center rounded bg-white/20 px-xs text-xs font-semibold">
              {code}
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
}
